import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Ship,
  Save,
  Eye,
  Send,
  Loader2,
  AlertTriangle,
  Ban,
  Info,
  FileText,
  History,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/toaster.jsx';
import { cn } from '@/lib/utils';
import { ESTADO_BADGE } from '../../utils/lclBoardConstants';
import {
  getInstrucciones,
  guardarInstrucciones,
  notificarProveedor,
  fetchPreviewInstruccionesUrl,
} from '../lclInstrucciones.service.js';
import ParteBlock from './ParteBlock.jsx';
import NotificarProveedorDialog from './NotificarProveedorDialog.jsx';

/** Sólo lo que edita el operador: el DTO rechaza cualquier otra cosa. */
function aPayload(f) {
  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  const parte = (p = {}) => ({
    razonSocial: p.razonSocial || '',
    nit: p.nit || '',
    direccion: p.direccion || '',
    disposicion: p.disposicion || null,
    disposicionDetalle: p.disposicionDetalle || '',
  });
  return {
    carga: {
      cliente: f.carga?.cliente || '',
      origen: f.carga?.origen || '',
      destino: f.carga?.destino || '',
      mercancia: f.carga?.mercancia || '',
      numPiezas: num(f.carga?.numPiezas),
      volumen: num(f.carga?.volumen),
      peso: num(f.carga?.peso),
      incoterm: f.carga?.incoterm || '',
    },
    shipper: {
      nombre: f.shipper?.nombre || '',
      direccion: f.shipper?.direccion || '',
      correo: f.shipper?.correo || '',
      telefono: f.shipper?.telefono || '',
    },
    consignatario: parte(f.consignatario),
    notify: {
      ...parte(f.notify),
      igualAlConsignatario: f.notify?.igualAlConsignatario !== false,
    },
    hsCode: f.hsCode || '',
    observaciones: f.observaciones || '',
  };
}

const fechaHora = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
};

function Campo({ id, label, className, children }) {
  return (
    <div className={cn('space-y-1', className)}>
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

function Seccion({ titulo, icono: Icono, children }) {
  return (
    <section className="rounded-lg border bg-background">
      <h3 className="flex items-center gap-2 border-b px-4 py-2.5 text-sm font-semibold">
        {Icono && <Icono className="h-4 w-4 text-primary" />}
        {titulo}
      </h3>
      <div className="p-4">{children}</div>
    </section>
  );
}

/**
 * Instrucciones de embarque de una solicitud LCL.
 *
 * Es el paso que inicia la operación: se arman los datos del embarque y
 * el corte del documento de transporte (shipper, consignatario, notify,
 * HS code), y se notifican al proveedor de la cotización seleccionada.
 * El primer envío real pasa la solicitud a Operaciones en curso.
 *
 * Se abre desde el tablero de Cotizaciones (para iniciar la operación) y
 * desde el de Operaciones en curso (para corregirlas o reenviarlas).
 *
 * `onChanged({ operacionIniciada })` avisa al tablero para que recargue:
 * si la operación arrancó, la solicitud ya no pertenece a Cotizaciones.
 */
export default function LclInstruccionesEmbarqueDialog({
  open,
  onOpenChange,
  lclRequestId,
  onChanged,
}) {
  const toast = useToast();

  const [ctx, setCtx] = useState(null);
  const [form, setForm] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [sucio, setSucio] = useState(false);
  const [tab, setTab] = useState('datos');
  const [notificarAbierto, setNotificarAbierto] = useState(false);
  const [confirmarCierre, setConfirmarCierre] = useState(false);
  const [preview, setPreview] = useState({ url: null, cargando: false, error: null });

  const cargar = useCallback(async () => {
    if (!lclRequestId) return;
    setCargando(true);
    setError(null);
    try {
      const r = await getInstrucciones(lclRequestId);
      if (!r?.success) throw new Error([].concat(r?.message || 'No se pudo cargar').join(' · '));
      setCtx(r.data);
      setForm(r.data.instruccion);
      setSucio(false);
    } catch (e) {
      setError(e?.message || 'No se pudieron cargar las instrucciones');
    } finally {
      setCargando(false);
    }
  }, [lclRequestId]);

  useEffect(() => {
    if (open) cargar();
  }, [open, cargar]);

  // Libera el blob de la vista previa cuando cambia o al desmontar.
  useEffect(() => () => preview.url && URL.revokeObjectURL(preview.url), [preview.url]);

  const set = (bloque, campo, valor) => {
    setSucio(true);
    setForm((f) =>
      campo === null
        ? { ...f, [bloque]: valor }
        : { ...f, [bloque]: { ...(f[bloque] || {}), [campo]: valor } },
    );
  };

  /** Devuelve true si quedó guardado. */
  const guardar = async ({ silencioso = false } = {}) => {
    setGuardando(true);
    try {
      const r = await guardarInstrucciones(lclRequestId, aPayload(form));
      if (r?.success) {
        setForm(r.data);
        setCtx((c) => ({ ...c, guardada: true, instruccion: r.data }));
        setSucio(false);
        if (!silencioso) toast.success('Guardado', 'Las instrucciones quedaron guardadas');
        return true;
      }
      toast.error('No se pudo guardar', [].concat(r?.message || '').join(' · '));
      return false;
    } catch (e) {
      toast.error('No se pudo guardar', e?.message);
      return false;
    } finally {
      setGuardando(false);
    }
  };

  const verPreview = async () => {
    setTab('preview');
    // La vista previa se genera con lo guardado: si hay cambios, se
    // guardan primero para que el PDF muestre lo que está en pantalla.
    if (sucio && !(await guardar({ silencioso: true }))) return;
    setPreview((p) => ({ ...p, cargando: true, error: null }));
    try {
      const url = await fetchPreviewInstruccionesUrl(lclRequestId);
      setPreview({ url, cargando: false, error: null });
    } catch (e) {
      setPreview({ url: null, cargando: false, error: e?.message });
    }
  };

  const enviar = async (datos) => {
    const r = await notificarProveedor(lclRequestId, { ...datos, instrucciones: aPayload(form) });
    if (r?.success) {
      setSucio(false);
      // El estado de la solicitud y la bitácora cambian: se recarga.
      const fresco = await getInstrucciones(lclRequestId);
      if (fresco?.success) {
        setCtx(fresco.data);
        setForm(fresco.data.instruccion);
      }
      onChanged?.({ operacionIniciada: !!r.data?.operacionIniciada });
    }
    return r;
  };

  // Cerrar con cambios sin guardar pide confirmación: se pierde trabajo
  // que suele ser largo de tipear (direcciones, NIT, partida).
  const intentarCerrar = (abierto) => {
    if (abierto) return;
    if (sucio) setConfirmarCierre(true);
    else onOpenChange?.(false);
  };

  const notificaciones = useMemo(
    () => [...(form?.notificaciones || [])].reverse(),
    [form?.notificaciones],
  );

  const s = ctx?.solicitud;
  const cot = ctx?.cotizacion;
  const prov = ctx?.proveedor;
  const igual = form?.notify?.igualAlConsignatario !== false;
  const enCurso = s?.estado === 'Enviada';

  return (
    <Dialog open={open} onOpenChange={intentarCerrar}>
      <DialogContent
        hideClose
        className="flex h-[calc(100vh-2rem)] w-[95vw] max-w-6xl flex-col gap-0 overflow-hidden p-0"
      >
        {/* Encabezado */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Ship className="h-5 w-5 text-primary" />
            </div>
            <div>
              <DialogTitle className="text-lg">
                {enCurso ? 'Instrucciones de embarque' : 'Iniciar operación · Instrucciones de embarque'}
              </DialogTitle>
              <DialogDescription className="text-sm">
                {s ? `${s.codigo} · ${s.clienteSnapshot?.nombre || '—'} · ${form?.numero || ''}` : ' '}
              </DialogDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {s && (
              <Badge
                variant="outline"
                className={cn('border font-normal', ESTADO_BADGE[s.estado] || '')}
              >
                {enCurso ? `En curso${s.subEstado ? ` · ${s.subEstado}` : ''}` : s.estado}
              </Badge>
            )}
            {form?.estado === 'Notificada' && (
              <Badge
                variant="outline"
                className="border-emerald-200 bg-emerald-50 font-normal text-emerald-800"
              >
                Proveedor notificado
              </Badge>
            )}
            {sucio && (
              <Badge
                variant="outline"
                className="border-amber-200 bg-amber-50 font-normal text-amber-800"
              >
                Cambios sin guardar
              </Badge>
            )}
            <Button
              variant="ghost"
              size="icon"
              aria-label="Cerrar"
              onClick={() => intentarCerrar(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Pestañas */}
        <div className="flex shrink-0 gap-1 border-b px-4">
          {[
            { id: 'datos', label: 'Datos y documento de transporte', icon: FileText, onClick: () => setTab('datos') },
            { id: 'preview', label: 'Vista previa del PDF', icon: Eye, onClick: verPreview },
          ].map((t) => {
            const I = t.icon;
            return (
              <button
                key={t.id}
                type="button"
                disabled={!form}
                onClick={t.onClick}
                className={cn(
                  'flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm transition-colors',
                  tab === t.id
                    ? 'border-primary font-medium text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                <I className="h-4 w-4" /> {t.label}
              </button>
            );
          })}
        </div>

        {/* Cuerpo: lo único que scrollea */}
        <div className="min-h-0 flex-1 overflow-y-auto bg-muted/20 p-4">
          {cargando && (
            <div className="flex items-center justify-center py-24 text-muted-foreground">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando instrucciones…
            </div>
          )}

          {!cargando && (error || !form) && (
            <div className="mx-auto max-w-lg space-y-3 py-24 text-center">
              <AlertTriangle className="mx-auto h-8 w-8 text-rose-600" />
              <p className="text-sm">{error || 'No se encontró la solicitud'}</p>
              <Button variant="outline" onClick={cargar}>
                Reintentar
              </Button>
            </div>
          )}

          {!cargando && form && tab === 'preview' && (
            <div className="flex h-full flex-col gap-2">
              <p className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                <Info className="h-3 w-3" />
                Esto es lo que recibe el proveedor adjunto al correo.
              </p>
              {preview.cargando && (
                <div className="flex min-h-0 flex-1 items-center justify-center rounded border bg-muted text-muted-foreground">
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Generando el PDF…
                </div>
              )}
              {!preview.cargando && preview.error && (
                <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 rounded border bg-muted text-sm text-rose-700">
                  <AlertTriangle className="h-6 w-6" />
                  {preview.error}
                  <Button size="sm" variant="outline" onClick={verPreview}>
                    Reintentar
                  </Button>
                </div>
              )}
              {!preview.cargando && !preview.error && preview.url && (
                <iframe
                  title="Vista previa de las instrucciones"
                  src={preview.url}
                  className="min-h-0 w-full flex-1 rounded border bg-muted"
                />
              )}
            </div>
          )}

          {!cargando && form && tab === 'datos' && (
            <div className="space-y-4">
              {/* Proveedor y avisos */}
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-lg border bg-background p-4">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    Cotización seleccionada
                  </p>
                  {cot ? (
                    <div className="mt-1 space-y-0.5">
                      <p className="font-medium">
                        {prov?.nombre || cot.proveedorSnapshot?.nombre || '—'}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {cot.numero}
                        {cot.tiempoTransito ? ` · tránsito ${cot.tiempoTransito}` : ''}
                      </p>
                      {ctx.destinatariosSugeridos?.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          Se notifica a: {ctx.destinatariosSugeridos.join(', ')}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-muted-foreground">Ninguna todavía.</p>
                  )}
                </div>
                <div className="space-y-2">
                  {!ctx.puedeNotificar && ctx.motivo && (
                    <div className="flex gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
                      <Ban className="mt-0.5 h-4 w-4 shrink-0" />
                      <div>
                        <p>{ctx.motivo}</p>
                        {!cot && (
                          <p className="mt-1 text-xs">
                            Puedes llenar y guardar las instrucciones igual; el envío se habilita
                            al seleccionar una cotización.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  {ctx.avisoProveedor && (
                    <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                      <p>{ctx.avisoProveedor}</p>
                    </div>
                  )}
                  {ctx.puedeNotificar && !ctx.avisoProveedor && (
                    <div className="flex gap-2 rounded-lg border bg-background p-3 text-sm text-muted-foreground">
                      <Info className="mt-0.5 h-4 w-4 shrink-0" />
                      <p>
                        {enCurso
                          ? 'La operación ya está en curso. Si corriges algo, puedes reenviar las instrucciones al proveedor.'
                          : 'Al notificar al proveedor, la solicitud pasa a Operaciones en curso.'}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Datos del embarque */}
              <Seccion titulo="Datos del embarque" icono={FileText}>
                <div className="grid gap-3 md:grid-cols-4">
                  <Campo id="c-cliente" label="Cliente" className="md:col-span-2">
                    <Input
                      id="c-cliente"
                      value={form.carga?.cliente || ''}
                      onChange={(e) => set('carga', 'cliente', e.target.value)}
                    />
                  </Campo>
                  <Campo id="c-incoterm" label="Término de negociación">
                    <Input
                      id="c-incoterm"
                      value={form.carga?.incoterm || ''}
                      onChange={(e) => set('carga', 'incoterm', e.target.value)}
                    />
                  </Campo>
                  <Campo id="c-piezas" label="Cantidad de piezas">
                    <Input
                      id="c-piezas"
                      type="number"
                      min="0"
                      value={form.carga?.numPiezas ?? ''}
                      onChange={(e) => set('carga', 'numPiezas', e.target.value)}
                    />
                  </Campo>
                  <Campo id="c-origen" label="Origen" className="md:col-span-2">
                    <Input
                      id="c-origen"
                      value={form.carga?.origen || ''}
                      onChange={(e) => set('carga', 'origen', e.target.value)}
                    />
                  </Campo>
                  <Campo id="c-destino" label="Destino" className="md:col-span-2">
                    <Input
                      id="c-destino"
                      value={form.carga?.destino || ''}
                      onChange={(e) => set('carga', 'destino', e.target.value)}
                    />
                  </Campo>
                  <Campo id="c-mercancia" label="Mercancía" className="md:col-span-2">
                    <Input
                      id="c-mercancia"
                      value={form.carga?.mercancia || ''}
                      onChange={(e) => set('carga', 'mercancia', e.target.value)}
                    />
                  </Campo>
                  <Campo id="c-volumen" label="Volumen (m³)">
                    <Input
                      id="c-volumen"
                      type="number"
                      min="0"
                      step="0.001"
                      value={form.carga?.volumen ?? ''}
                      onChange={(e) => set('carga', 'volumen', e.target.value)}
                    />
                  </Campo>
                  <Campo id="c-peso" label="Peso (kg)">
                    <Input
                      id="c-peso"
                      type="number"
                      min="0"
                      step="0.01"
                      value={form.carga?.peso ?? ''}
                      onChange={(e) => set('carga', 'peso', e.target.value)}
                    />
                  </Campo>
                </div>
              </Seccion>

              {/* Corte tipo BL */}
              <Seccion titulo="Datos para el documento de transporte (BL)" icono={Ship}>
                <div className="grid gap-4 lg:grid-cols-2">
                  <div className="rounded-lg border">
                    <div className="rounded-t-lg bg-zinc-800 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white">
                      Shipper
                    </div>
                    <div className="grid gap-3 p-3 sm:grid-cols-2">
                      <Campo id="s-nombre" label="Nombre" className="sm:col-span-2">
                        <Input
                          id="s-nombre"
                          value={form.shipper?.nombre || ''}
                          onChange={(e) => set('shipper', 'nombre', e.target.value)}
                        />
                      </Campo>
                      <Campo id="s-dir" label="Dirección" className="sm:col-span-2">
                        <Input
                          id="s-dir"
                          value={form.shipper?.direccion || ''}
                          onChange={(e) => set('shipper', 'direccion', e.target.value)}
                        />
                      </Campo>
                      <Campo id="s-correo" label="Email">
                        <Input
                          id="s-correo"
                          type="email"
                          value={form.shipper?.correo || ''}
                          onChange={(e) => set('shipper', 'correo', e.target.value)}
                        />
                      </Campo>
                      <Campo id="s-tel" label="Teléfono">
                        <Input
                          id="s-tel"
                          value={form.shipper?.telefono || ''}
                          onChange={(e) => set('shipper', 'telefono', e.target.value)}
                        />
                      </Campo>
                    </div>
                  </div>

                  <ParteBlock
                    idPrefix="cons"
                    titulo="Consignatario"
                    valor={form.consignatario}
                    onChange={(v) => set('consignatario', null, v)}
                  />

                  <ParteBlock
                    idPrefix="notify"
                    titulo="Notify"
                    valor={igual ? form.consignatario : form.notify}
                    disabled={igual}
                    onChange={(v) => set('notify', null, { ...v, igualAlConsignatario: false })}
                  >
                    <label className="flex cursor-pointer items-center gap-1.5 text-[11px] font-normal normal-case tracking-normal">
                      <input
                        type="checkbox"
                        checked={igual}
                        onChange={(e) => {
                          const marcado = e.target.checked;
                          // Al desmarcar se arranca con una copia del
                          // consignatario, que suele ser casi igual.
                          set(
                            'notify',
                            null,
                            marcado
                              ? { ...(form.notify || {}), igualAlConsignatario: true }
                              : { ...(form.consignatario || {}), igualAlConsignatario: false },
                          );
                        }}
                      />
                      Igual al consignatario
                    </label>
                  </ParteBlock>

                  <div className="space-y-3 rounded-lg border p-3">
                    <Campo id="hs" label="HS Code (partida arancelaria)">
                      <Input
                        id="hs"
                        value={form.hsCode || ''}
                        placeholder="0000.00.00.00"
                        onChange={(e) => set('hsCode', null, e.target.value)}
                      />
                    </Campo>
                    <Campo id="obs" label="Observaciones (opcional)">
                      <Textarea
                        id="obs"
                        rows={4}
                        value={form.observaciones || ''}
                        onChange={(e) => set('observaciones', null, e.target.value)}
                      />
                    </Campo>
                  </div>
                </div>
              </Seccion>

              {/* Bitácora */}
              {notificaciones.length > 0 && (
                <Seccion titulo="Envíos al proveedor" icono={History}>
                  <ul className="divide-y text-sm">
                    {notificaciones.map((n, i) => (
                      <li key={i} className="flex flex-wrap items-center justify-between gap-2 py-2">
                        <span>
                          {fechaHora(n.fecha)} · {(n.para || []).join(', ')}
                          {n.cc?.length ? ` · cc ${n.cc.join(', ')}` : ''}
                        </span>
                        <span className="flex items-center gap-2">
                          {n.inicioOperacion && (
                            <Badge
                              variant="outline"
                              className="border-blue-200 bg-blue-50 font-normal text-blue-800"
                            >
                              inició la operación
                            </Badge>
                          )}
                          <Badge
                            variant="outline"
                            className={
                              n.modo === 'smtp'
                                ? 'border-emerald-200 bg-emerald-50 font-normal text-emerald-800'
                                : 'border-amber-200 bg-amber-50 font-normal text-amber-800'
                            }
                            title={n.modo === 'smtp' ? 'Enviado' : 'Modo de prueba: no salió'}
                          >
                            {n.modo === 'smtp' ? 'enviado' : 'dry-run'}
                          </Badge>
                        </span>
                      </li>
                    ))}
                  </ul>
                </Seccion>
              )}
            </div>
          )}
        </div>

        {/* Pie */}
        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 border-t p-4">
          <Button
            variant="outline"
            onClick={() => guardar()}
            disabled={!form || guardando || !sucio}
          >
            {guardando ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Guardar
          </Button>
          <Button
            onClick={() => setNotificarAbierto(true)}
            disabled={!form || !ctx?.puedeNotificar || guardando}
            title={ctx?.puedeNotificar ? '' : ctx?.motivo || ''}
          >
            <Send className="mr-2 h-4 w-4" />
            {enCurso ? 'Reenviar al proveedor' : 'Notificar al proveedor'}
          </Button>
        </div>
      </DialogContent>

      <NotificarProveedorDialog
        open={notificarAbierto}
        onOpenChange={setNotificarAbierto}
        contexto={ctx}
        onEnviar={enviar}
      />

      <AlertDialog open={confirmarCierre} onOpenChange={setConfirmarCierre}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cerrar sin guardar?</AlertDialogTitle>
            <AlertDialogDescription>
              Hay cambios en las instrucciones que todavía no guardaste. Si cierras, se pierden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Seguir editando</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmarCierre(false);
                onOpenChange?.(false);
              }}
            >
              Descartar cambios
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
